import { Link } from 'react-router-dom';
import { Eyebrow } from '@busbook/ui';
import { useTenant } from '../lib/tenant.js';
import { useT } from '../i18n/index.js';

/**
 * Help and About (docs/05). Both render tenant-provided text; the operator's own
 * contact details come first, because someone opening Help usually has a problem
 * right now.
 */

/**
 * Tenant rich text is rendered as plain paragraphs rather than raw HTML. A
 * tenant is not hostile, but their content is still untrusted input, and
 * injecting it into the passenger app would make one operator's editor a way to
 * run script in another passenger's session.
 */
function RichText({ value }: { value: string }) {
  const paragraphs = value
    .replace(/<[^>]+>/g, ' ')
    .split(/\n{2,}|\r\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return null;

  return (
    <div className="mt-3 flex flex-col gap-3 text-sm" style={{ color: 'var(--color-muted)' }}>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  );
}

export function Help() {
  const t = useT();
  const { branding, settings } = useTenant();

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('c.help')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">
        {t('p.contact', { operator: branding.appName })}
      </h1>

      <div
        className="mt-4 rounded-xl border p-4"
        style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      >
        {settings.supportEmail || settings.supportPhone ? (
          <dl className="flex flex-col gap-3 text-sm">
            {settings.supportEmail && (
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('c.email')}</dt>
                <dd>
                  <a
                    href={`mailto:${settings.supportEmail}`}
                    className="underline underline-offset-4"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {settings.supportEmail}
                  </a>
                </dd>
              </div>
            )}
            {settings.supportPhone && (
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('c.phone')}</dt>
                <dd>
                  <a
                    href={`tel:${settings.supportPhone.replace(/\s+/g, '')}`}
                    className="underline underline-offset-4"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {settings.supportPhone}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            {branding.appName} has not published contact details yet.
          </p>
        )}
      </div>

      <div className="mt-7">
        <h2 className="text-sm font-semibold">{t('p.cancellations')}</h2>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('p.cancelPolicy', {
            hours: settings.cancellationCutoffHours,
            action: t('d.requestRefund'),
            operator: branding.appName,
          })}
        </p>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('p.unpaidReleased', { minutes: settings.paymentWindowMinutes })}
        </p>
      </div>

      {settings.faqHtml && (
        <div className="mt-7">
          <h2 className="text-sm font-semibold">{t('p.questions')}</h2>
          <RichText value={settings.faqHtml} />
        </div>
      )}

      <div className="mt-7">
        <h2 className="text-sm font-semibold">{t('p.findingBooking')}</h2>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          Booked as a guest? Go to{' '}
          <Link to="/bookings" className="underline underline-offset-4" style={{ color: 'var(--color-primary)' }}>
            {t('c.myTrips')}
          </Link>{' '}
          and enter your reference and email — we will send a link to your ticket.
        </p>
      </div>

      {/* The account block that used to close this page has gone to the app chrome, where
          the staff consoles have always kept it. It was also the last place still offering
          to "carry on booking as a guest", which stopped being true at DECISIONS D68. */}
    </section>
  );
}

export function About() {
  const t = useT();
  const { branding, settings } = useTenant();

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('c.about')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{branding.appName}</h1>
      {settings.aboutHtml ? (
        <RichText value={settings.aboutHtml} />
      ) : (
        <p className="mt-3 text-sm" style={{ color: 'var(--color-muted)' }}>
          {branding.appName} has not published anything here yet.
        </p>
      )}
      <p className="mt-6 text-xs" style={{ color: 'var(--color-faint)' }}>
        {t('p.faresTimes', { currency: settings.currency, tz: settings.timezone.replace(/_/g, ' ') })}
      </p>
    </section>
  );
}
